import { inject, Injectable } from '@angular/core';
import { LocalizationVm as LocalizationAPIVm, LocType, PortalType } from '@ats/api/cm';
import { CmFeatureService } from '@ats/cm/core';
import { ClientService } from '@cm/core/client';
import { CultureService } from '@cm/core/culture';

import { SettingsService } from '@cm/core/settings';
import {
	BehaviorSubject,
	combineLatest,
	firstValueFrom,
	forkJoin,
	from,
	map,
	Observable,
	of,
	switchMap,
	take,
} from 'rxjs';
import { LocalizationVM, LocDef, LocPortal, populateClientLocData } from '../models';
import { ClientLocalizationCreateGql } from './client-localization-create-gql.service';
import { ClientLocalizationDeleteGql } from './client-localization-delete-gql.service';
import { ClientLocalizationGql } from './client-localization-gql.service';
import { ClientLocalizationUpdateGql } from './client-localization-update-gql.service';

@Injectable({
	providedIn: 'root',
})
export class LocalizationCoreService {
	private readonly locStore = new Map<string, LocalizationVM>();
	private locStoreObs = new BehaviorSubject<Map<string, LocalizationVM>>(this.locStore);

	readonly all$ = this.locStoreObs.asObservable();

	private readonly gql = {
		query: inject(ClientLocalizationGql),
		create: inject(ClientLocalizationCreateGql),
		update: inject(ClientLocalizationUpdateGql),
		delete: inject(ClientLocalizationDeleteGql),
	};

	constructor(
		private readonly clientService: ClientService,
		private readonly cultureService: CultureService,
		private readonly featureService: CmFeatureService,
		private readonly settingsService: SettingsService
	) {}

	load(locs: Record<string, LocDef>, reloadExistingLocs: boolean = false): Promise<void> {
		const locsToLoad: (LocDef & { name: string })[] = Object.keys(locs).map(key => ({
			...locs[key],
			name: key,
		}));

		return this.addToOrUpdateLocStore(locsToLoad, reloadExistingLocs);
	}

	reload(locName: string | string[]): Promise<void> {
		if (typeof locName === 'string') {
			const loc = this.locStore.get(locName);
			if (!loc) {
				return Promise.resolve();
			}
			return this.addToOrUpdateLocStore([loc], true);
		} else {
			const locs = locName
				.map(name => this.locStore.get(name))
				.filter(loc => loc !== undefined);
			return this.addToOrUpdateLocStore(locs, true);
		}
	}

	localization$ = (name: string): Observable<LocalizationVM | null> => {
		return this.locStoreObs.asObservable().pipe(map(store => store.get(name) || null));
	};

	localizationArray$ = (names: string[]): Observable<LocalizationVM[]> =>
		combineLatest(names.map(name => this.localization$(name))).pipe(
			map(locs => locs.filter(loc => loc !== null))
		);

	localizationMap$ = (names: string[]): Observable<Record<string, LocalizationVM>> =>
		this.localizationArray$([...names]).pipe(
			map(locs => locs.reduce((acc, loc) => ({ ...acc, [loc.name]: loc }), {}))
		);

	createOrUpdateClientLoc$(
		loc: LocalizationAPIVm,
		culture?: string
	): Observable<LocalizationVM | LocalizationVM[] | null> {
		const culture$ = culture
			? of(culture)
			: this.cultureService.culture$.pipe(map(c => c.code));
		const locDef = this.locStore.get(loc.name);

		if (!locDef) {
			throw new Error(`Localization ${loc.name} not found`);
		}

		return culture$.pipe(
			switchMap(cultureName => {
				// Process both portals if the loc is for all
				if (locDef.portal === 'All') {
					return combineLatest([
						this.createOrUpdateMutation$(
							loc,
							cultureName,
							LocType.CandidatePortalString
						),
						this.createOrUpdateMutation$(loc, cultureName, LocType.SponsorPortalString),
					]);
				} else if (locDef.portal === 'CP') {
					return this.createOrUpdateMutation$(
						loc,
						cultureName,
						LocType.CandidatePortalString
					);
				} else {
					return this.createOrUpdateMutation$(
						loc,
						cultureName,
						LocType.SponsorPortalString
					);
				}
			}),
			switchMap(locs => {
				if (!locs) {
					return of(null);
				}
				if (!Array.isArray(locs)) {
					return from(this.reload([locs])).pipe(
						switchMap(() => this.localization$(locs))
					);
				}
				const locsToReload = locs.filter(loc => loc !== null) as string[];
				return from(this.reload(locsToReload)).pipe(
					switchMap(() => this.localizationArray$(locsToReload))
				);
			})
		);
	}

	deleteClientLoc$(name: string, culture?: string): Observable<boolean> {
		const culture$ = culture
			? of(culture)
			: this.cultureService.culture$.pipe(map(c => c.code));
		const locDef = this.locStore.get(name);
		if (!locDef) {
			throw new Error(`Localization ${name} not found`);
		}

		return culture$.pipe(
			map(cultureName => ({
				cultureName,
				localizationList: [
					{
						name,
						value: '',
					},
				],
			})),
			map(request => {
				if (locDef.portal === 'All') {
					return [
						{
							...request,
							type: LocType.CandidatePortalString,
						},
						{
							...request,
							type: LocType.SponsorPortalString,
						},
					];
				} else {
					return [
						{
							...request,
							type:
								locDef.portal === 'CP'
									? LocType.CandidatePortalString
									: LocType.SponsorPortalString,
						},
					];
				}
			}),
			switchMap(requests =>
				forkJoin(requests.map(request => this.gql.delete.delete$(request)))
			),
			switchMap(results => from(this.reload([name])).pipe(map(() => results))),
			map(results => results.every(result => result))
		);
	}

	private clientLocExists$(
		loc: LocalizationAPIVm,
		culture: string,
		type: LocType
	): Observable<boolean> {
		return this.clientService.client$.pipe(
			switchMap(clientDbAlias =>
				this.gql.query.fetch({
					request: {
						clientDbAlias,
						language: culture,
						filterNotFound: true,
						localizations: [{ name: loc.name, type }],
					},
				})
			),
			map(
				({ data }) =>
					!!(data?.clientLocalizationList || []).find(cl => cl.name === loc.name)
			)
		);
	}

	private createOrUpdateMutation$(
		loc: LocalizationAPIVm,
		culture: string,
		type: LocType
	): Observable<string | null> {
		return this.clientLocExists$(loc, culture, type).pipe(
			switchMap(locExists =>
				locExists
					? this.gql.update.update$({
							cultureName: culture,
							type,
							localizationList: [loc],
					  })
					: this.gql.create.create$({
							cultureName: culture,
							type,
							localizationList: [loc],
					  })
			),
			map(locs => locs[0] || null)
		);
	}

	private addToOrUpdateLocStore(
		locsToLoad: (LocDef & { name: string })[],
		reloadExistingLocs: boolean = false
	): Promise<void> {
		// Start with our features
		return firstValueFrom(
			this.featureService.features$.pipe(
				map(features =>
					locsToLoad.filter(
						loc =>
							// If we are reloading existing locs, or the loc is not in our store, then we need to load it
							reloadExistingLocs ||
							(!this.locStore.has(loc.name) &&
								// If the loc has a feature, then we need to check if it is in our features
								(!loc.feature || features.includes(loc.feature)))
					)
				),
				switchMap(locs => (locs.length > 0 ? this.populateClientLocData(locs) : of([]))),
				take(1)
			)
		).then(locs => {
			if (locs.length > 0) {
				this.manageLocStore(locs);
			}
			return;
		});
	}

	private populateClientLocData(
		definitions: (LocDef & { name: string })[]
	): Observable<LocalizationVM[]> {
		return combineLatest([this.clientService.client$, this.cultureService.culture$]).pipe(
			map(([clientDbAlias, { code }]) => ({
				clientDbAlias,
				language: code,
				filterNotFound: true,
				localizations: definitions.map(loc => ({
					name: loc.name,
					type:
						this.getPortalType(loc) === 'CP'
							? LocType.CandidatePortalString
							: LocType.SponsorPortalString,
				})),
			})),
			// Get our client localizations
			switchMap(request =>
				this.gql.query.fetch({ request }, { fetchPolicy: 'network-only' })
			),
			// Populate our localization data
			map(({ data }) =>
				definitions.map(loc => {
					return populateClientLocData(
						loc,
						(data?.clientLocalizationList || []).find(cl => cl.name === loc.name) ||
							loc.name
					);
				})
			)
		);
	}

	private getPortalType = (loc: LocDef): Omit<LocPortal, 'All'> => {
		if (loc.portal === 'All') {
			return this.settingsService.portal === PortalType.CandidatePortal ? 'CP' : 'SP';
		}
		return loc.portal;
	};

	private manageLocStore(locs: LocalizationVM[]): void {
		locs.forEach(loc => {
			this.locStore.set(loc.name, loc);
		});
		this.locStoreObs.next(this.locStore);
	}
}
